import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { unitConvert } from '@farmtrx/units'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import ModalContainer from 'components/modal/modal-container'
import ModalActions from 'components/modal/modal-actions'
import { closeModal } from 'actions/app'
import { createLegend, generateCropLegends, updateLegend } from 'api/legend'
import { palettes } from 'common/custom-legend/palettes'
import { isSubscriptionValid } from 'common/misc'
import EqualRangeLegend from 'components/layer-legend/equal-range-legend'
import LegendHistogram from 'components/layer-legend/legend-histogram'
import { TEST_IDS } from 'test-ids'
import type { AppState } from 'state/index'
import type { CustomLegend } from 'state/field'

const CROP_PARAMETERS = ['yield', 'moisture', 'speed', 'header_height']
const DEM_PARAMETER = 'dem'
// Matches the class counts the installed GeoServer SLDs and the palettes support.
const RANGE_OPTIONS = {
  yield: [6, 7, 8, 9, 10],
  moisture: [6, 7, 8, 9],
  speed: [6, 7, 8, 9],
  header_height: [6, 7, 8, 9],
  dem: [6],
}

// The axis labels are 14px, so each bar needs about this much room to stay legible.
const HISTOGRAM_BAR_HEIGHT = 24
const HISTOGRAM_MIN_HEIGHT = 200

const FIELD_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 2,
}

// The codes POST /legend/crop-legends/generate documents. Anything else falls
// back to the server's localised `reason`, then to a generic message.
const ERROR_MESSAGES: Record<string, string> = {
  NO_ACTIVE_SUBSCRIPTION: 'legend_no_active_subscription',
  CROP_YEAR_NOT_SUBSCRIBED: 'legend_crop_year_not_subscribed',
  UNSUPPORTED_PARAMETER: 'legend_unsupported_parameter',
  DEM_PARAMETER_NOT_COMBINABLE: 'legend_dem_parameter_not_combinable',
  CROP_YEAR_REQUIRED: 'legend_crop_year_required',
  INVALID_PERCENTILES: 'legend_invalid_percentiles',
  UNSUPPORTED_NUM_RANGES: 'legend_unsupported_num_ranges',
}

type Proposal = {
  cropId: number | null
  cropName: string | null
  cropYear: number | null
  parameter: string
  numRanges: number
  endpoints: { low: number; high: number }
  breaks: number[]
  pooledHistogram: { counts: number[]; values: number[] }
  styleType?: string
  support: { harvestCount: number; pointCount: number }
  warnings: string[]
}

type Skipped = {
  cropId: number | null
  cropName: string | null
  parameter: string
  reason: string
  harvestCount?: number
  harvestsConsidered?: number
  outliersRejected?: number
  minHarvests?: number
}

type Diagnostics = {
  harvestsConsidered: number
  harvestsWithLegendData: number
  outliersRejected: number
  excludedYears: number[]
}

// The API deliberately returns breaks without colours, so palette definitions
// live only here. Mirrors createPalettes() in edit-legend.tsx.
const paletteFamily = (parameter: string) => {
  if (parameter === DEM_PARAMETER) {
    return { ...palettes.dem, ...palettes.general }
  }
  if (parameter === 'yield') {
    return { ...palettes.yield, ...palettes.general }
  }
  return { ...palettes[parameter], ...palettes.general }
}

const defaultColours = (parameter: string, numRanges: number) => {
  const family = paletteFamily(parameter)
  const palette = Object.keys(family)[0]
  return { palette, colours: family[palette]?.[numRanges] ?? [] }
}

const parseCutoff = (value: string) => (value === '' ? '' : Number(value))

const proposalKey = (proposal: Proposal) =>
  `${proposal.cropId ?? 'global'}-${proposal.cropYear ?? 'all'}-${proposal.parameter}`

export default function GenerateCropLegends(props: {
  demOnly?: boolean
  onGenerated?: () => void
}) {
  const { demOnly = false, onGenerated } = props
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const harvestYears = useSelector((state: AppState) => state.harvest.years)
  const subscriptions = useSelector(
    (state: AppState) => state.account.subscriptions,
  )
  const cropLegends: CustomLegend[] = useSelector(
    (state: AppState) => state.field.cropLegends,
  )
  const preferenceUnits = useSelector(
    (state: AppState) => state.account.singleton.prefs?.units,
  )
  const cropConstants = useSelector(
    (state: AppState) => state.crop['cropConstantsCollection'],
  )

  // Only seasons covered by a subscription can be pooled, so offering the others
  // would just produce a 403.
  const availableYears = useMemo(() => {
    const years = (harvestYears || [])
      .filter((y) => y.hasHarvest)
      .map((y) => y.harvestYr)
    return Array.from(new Set(years))
      .filter((year) =>
        isSubscriptionValid(subscriptions, `${year}-01-01`, `${year}-12-31`),
      )
      .sort((a, b) => b - a)
  }, [harvestYears, subscriptions])

  const [cropYear, setCropYear] = useState<number | ''>('')
  const [parameters, setParameters] = useState<string[]>(['yield'])
  const [numRanges, setNumRanges] = useState<number>(10)
  const [lowPercentile, setLowPercentile] = useState<number | ''>(5)
  const [highPercentile, setHighPercentile] = useState<number | ''>(95)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [proposals, setProposals] = useState<Proposal[] | null>(null)
  const [skipped, setSkipped] = useState<Skipped[]>([])
  const [diagnostics, setDiagnostics] = useState<Diagnostics | null>(null)
  const [applied, setApplied] = useState<string[]>([])
  // Saves in flight. The API upserts by scope, so a second click cannot create a
  // duplicate legend, but it can still race the first — and the button reading
  // "Apply" through a slow save invites the click.
  const [saving, setSaving] = useState<string[]>([])

  useEffect(() => {
    if (cropYear === '' && availableYears.length > 0) {
      setCropYear(availableYears[0])
    }
  }, [availableYears, cropYear])

  // Clamp the class count whenever the selection no longer allows the current one
  // (yield goes to 10, the swath parameters only to 9).
  const allowedRanges = useMemo(() => {
    if (demOnly) return RANGE_OPTIONS.dem
    const perParameter = parameters.map((p) => RANGE_OPTIONS[p] ?? [])
    if (perParameter.length === 0) return RANGE_OPTIONS.yield
    return perParameter.reduce((shared, options) =>
      shared.filter((option) => options.includes(option)),
    )
  }, [demOnly, parameters])

  useEffect(() => {
    if (allowedRanges.length > 0 && !allowedRanges.includes(numRanges)) {
      setNumRanges(allowedRanges[allowedRanges.length - 1])
    }
  }, [allowedRanges, numRanges])

  const toggleParameter = (parameter: string) => {
    setProposals(null)
    setParameters((current) =>
      current.includes(parameter)
        ? current.filter((p) => p !== parameter)
        : [...current, parameter],
    )
  }

  const run = useCallback(() => {
    setLoading(true)
    setError(null)
    setApplied([])
    const params = demOnly
      ? { parameters: [DEM_PARAMETER], lowPercentile, highPercentile }
      : { cropYear, parameters, numRanges, lowPercentile, highPercentile }
    //@ts-ignore
    dispatch(generateCropLegends(params))
      .then((result) => {
        setProposals(result?.proposals ?? [])
        setSkipped(result?.skipped ?? [])
        setDiagnostics(result?.diagnostics ?? null)
      })
      .catch((err) => {
        // The request suppresses the global alert modal so this dialog survives
        // a rejection with its inputs intact; that makes rendering the error
        // here the only thing telling the user what to change.
        //
        // Match on the documented code, not the prose: `reason` is already
        // localised server-side and is not a stable identifier.
        const { code, reason } = err?.response?.data ?? {}
        setError(
          (code && ERROR_MESSAGES[code] && t(ERROR_MESSAGES[code])) ||
            reason ||
            t('legend_generate_failed'),
        )
        // Proposals from an earlier run describe inputs that have since been
        // rejected, so leaving them under the error invites applying them.
        setProposals(null)
        setSkipped([])
        setDiagnostics(null)
      })
      .finally(() => setLoading(false))
  }, [
    demOnly,
    dispatch,
    cropYear,
    parameters,
    numRanges,
    lowPercentile,
    highPercentile,
    t,
  ])

  /*
   * Saving leaves the dialog open so several proposals can be applied in one
   * sitting; the per-proposal button switches to "Saved". Only Apply all closes,
   * once the batch is done.
   */
  // A legend already saved for this exact scope, if any. Memoized on cropLegends
  // so `apply` re-reads it after a save rather than closing over a stale list.
  const existingLegendFor = useCallback(
    (proposal: Proposal) =>
      (cropLegends || []).find(
        (legend) =>
          legend.cropId === proposal.cropId &&
          legend.parameter === proposal.parameter &&
          legend.cropYear === proposal.cropYear,
      ),
    [cropLegends],
  )

  const apply = useCallback(
    (proposal: Proposal) => {
      const { palette, colours } = defaultColours(
        proposal.parameter,
        proposal.numRanges,
      )
      const body = {
        parameter: proposal.parameter,
        cropId: proposal.cropId,
        harvestId: null,
        cropYear: proposal.cropYear,
        legendData: {
          ranges: { c: colours, q: proposal.breaks },
          palette,
          num_ranges: proposal.numRanges,
          ...(proposal.parameter === DEM_PARAMETER
            ? { styleType: proposal.styleType }
            : {}),
        },
      }
      // Regenerating a scope replaces its legend rather than adding another.
      // The service upserts by scope too, so this is about hitting the right
      // endpoint when we already know the id, not the only thing standing
      // between us and duplicates.
      const existing = existingLegendFor(proposal)
      const save = existing
        ? updateLegend(existing.id, body)
        : createLegend(body)
      const key = proposalKey(proposal)
      setSaving((current) => [...current, key])
      //@ts-ignore
      return dispatch(save)
        .then(() => {
          setApplied((current) => [...current, key])
          if (onGenerated) onGenerated()
        })
        .finally(() => setSaving((current) => current.filter((k) => k !== key)))
    },
    [dispatch, existingLegendFor, onGenerated],
  )

  const applyAll = useCallback(async () => {
    for (const proposal of proposals ?? []) {
      const key = proposalKey(proposal)
      if (applied.includes(key) || saving.includes(key)) continue
      await apply(proposal)
    }
    dispatch(closeModal())
  }, [apply, applied, dispatch, proposals, saving])

  const toPreferredUnits = (parameter: string, value: number, cropId) => {
    switch (parameter) {
      case DEM_PARAMETER:
        return unitConvert.convertLength(
          value,
          preferenceUnits?.['length_in'],
          unitConvert.METERS,
        )
      case 'yield':
        return unitConvert.convertYieldToPref(
          value,
          preferenceUnits?.['yieldunit_in'],
          buPerTonneFor(cropId),
        )
      case 'speed':
        return unitConvert.convertSpeed(
          value,
          preferenceUnits?.['speed_in'],
          unitConvert.KPH,
        )
      case 'header_height':
        return unitConvert.convertLength(
          value,
          preferenceUnits?.['length_in'],
          unitConvert.MM,
        )
      default:
        return value
    }
  }

  const buPerTonneFor = (cropId: number | null) =>
    cropConstants?.find((c) => c.id === cropId)?.buPerTonne ?? 1.0

  const canRun =
    lowPercentile !== '' &&
    highPercentile !== '' &&
    (demOnly || (cropYear !== '' && parameters.length > 0))

  return (
    <ModalContainer>
      <Box
        data-testid={TEST_IDS.generateCropLegendsDialog}
        sx={{ minWidth: '520px', maxHeight: '60vh', overflowY: 'auto' }}
      >
        {/* The modal header already shows the title showModal was given. */}
        <Typography variant="body2" sx={{ color: 'gray' }}>
          {t(
            demOnly
              ? 'generate_dem_legend_description'
              : 'generate_crop_legends_description',
          )}
        </Typography>

        <Box sx={{ ...FIELD_GRID_SX, marginTop: '1.5rem' }}>
          {!demOnly && (
            <TextField
              select
              size="small"
              label={t('crop_year')}
              value={cropYear}
              onChange={(e) => {
                setProposals(null)
                setCropYear(Number(e.target.value))
              }}
              data-testid={TEST_IDS.generateCropLegendsYearSelect}
              fullWidth
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Tooltip title={t('legend_low_percentile')}>
            <TextField
              size="small"
              type="number"
              label={t('legend_low_percentile')}
              value={lowPercentile}
              onChange={(e) => {
                setProposals(null)
                setLowPercentile(parseCutoff(e.target.value))
              }}
              data-testid={TEST_IDS.generateCropLegendsLowPercentileInput}
              slotProps={{ htmlInput: { min: 2, max: 98 } }}
              fullWidth
            />
          </Tooltip>
          <TextField
            select
            size="small"
            label={t('legend_ranges')}
            value={numRanges}
            onChange={(e) => {
              setProposals(null)
              setNumRanges(Number(e.target.value))
            }}
            data-testid={TEST_IDS.generateCropLegendsRangeSelect}
            fullWidth
          >
            {allowedRanges.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title={t('legend_high_percentile')}>
            <TextField
              size="small"
              type="number"
              label={t('legend_high_percentile')}
              value={highPercentile}
              onChange={(e) => {
                setProposals(null)
                setHighPercentile(parseCutoff(e.target.value))
              }}
              data-testid={TEST_IDS.generateCropLegendsHighPercentileInput}
              slotProps={{ htmlInput: { min: 2, max: 98 } }}
              fullWidth
            />
          </Tooltip>
        </Box>
        <Typography
          variant="caption"
          sx={{ color: 'gray', display: 'block', marginTop: '0.5rem' }}
        >
          {t('legend_percentile_help')}
        </Typography>

        {!demOnly && (
          <Box
            sx={{ marginTop: '1rem' }}
            data-testid={TEST_IDS.generateCropLegendsParameterSelect}
          >
            <Typography variant="body2">{t('legend_parameters')}</Typography>
            {CROP_PARAMETERS.map((parameter) => (
              <FormControlLabel
                key={parameter}
                control={
                  <Checkbox
                    checked={parameters.includes(parameter)}
                    onChange={() => toggleParameter(parameter)}
                    slotProps={{ input: { 'aria-label': t(parameter) } }}
                  />
                }
                label={t(parameter)}
              />
            ))}
          </Box>
        )}

        {error ? (
          <Alert
            severity="error"
            sx={{ marginTop: '1rem' }}
            data-testid={TEST_IDS.generateCropLegendsError}
          >
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', margin: '2rem' }}
          >
            <CircularProgress aria-label={t('loading')} />
          </Box>
        ) : null}

        {proposals !== null && !loading ? (
          <Box sx={{ marginTop: '1.5rem' }}>
            {proposals.length === 0 ? (
              <Typography>{t('legend_no_proposals')}</Typography>
            ) : null}

            {proposals.map((proposal) => {
              const key = proposalKey(proposal)
              const { colours } = defaultColours(
                proposal.parameter,
                proposal.numRanges,
              )
              const existing = existingLegendFor(proposal)
              return (
                <Box key={key} sx={{ marginBottom: '1.5rem' }}>
                  <Divider sx={{ marginBottom: '0.75rem' }} />
                  <Typography variant="subtitle1">
                    {proposal.cropName
                      ? `${t(proposal.cropName)} — ${t(proposal.parameter)}`
                      : t(proposal.parameter)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'gray' }}>
                    {t('legend_proposal_support', {
                      harvests: proposal.support.harvestCount,
                      points: proposal.support.pointCount.toLocaleString(),
                    })}
                  </Typography>

                  <LegendHistogram
                    height={Math.max(
                      HISTOGRAM_MIN_HEIGHT,
                      proposal.pooledHistogram.counts.length *
                        HISTOGRAM_BAR_HEIGHT,
                    )}
                    data={proposal.pooledHistogram.counts.map(
                      (count, index) => {
                        const value = Number(
                          toPreferredUnits(
                            proposal.parameter,
                            proposal.pooledHistogram.values[index],
                            proposal.cropId,
                          ),
                        )
                        return { label: value.toFixed(1), count, value }
                      },
                    )}
                  />

                  <EqualRangeLegend
                    colors={colours}
                    quantities={proposal.breaks}
                    layer={{
                      name: proposal.parameter,
                      translation: proposal.parameter,
                    }}
                    numRanges={proposal.numRanges}
                    cropBuPerTonne={buPerTonneFor(proposal.cropId)}
                    selectedSwathParameter={proposal.parameter}
                    showUnits
                  />

                  {proposal.warnings.map((warning) => (
                    <Alert
                      key={warning}
                      severity="info"
                      sx={{ marginTop: '0.5rem' }}
                    >
                      {t(`legend_warning_${warning}`)}
                    </Alert>
                  ))}

                  <Button
                    variant="contained"
                    size="small"
                    sx={{ marginTop: '0.75rem' }}
                    disabled={applied.includes(key) || saving.includes(key)}
                    onClick={() => apply(proposal)}
                    data-testid={`${TEST_IDS.generateCropLegendsApplyButton}-${key}`}
                  >
                    {t(
                      applied.includes(key)
                        ? 'saved'
                        : saving.includes(key)
                          ? 'applying'
                          : existing
                            ? 'replace'
                            : 'apply',
                    )}
                  </Button>
                </Box>
              )
            })}

            {skipped.map((entry) => (
              <Typography
                key={`${entry.cropId}-${entry.parameter}`}
                variant="caption"
                sx={{ display: 'block', color: 'gray' }}
              >
                {entry.cropName ? `${t(entry.cropName)} — ` : ''}
                {t(entry.parameter)}:{' '}
                {t(`legend_skipped_${entry.reason}`, {
                  count: entry.harvestCount ?? 0,
                  required: entry.minHarvests ?? 2,
                })}
              </Typography>
            ))}

            {/* Without this, a low harvest count looks like an invisible filter
                rather than missing legend data on the layers. */}
            {diagnostics ? (
              <Typography
                variant="caption"
                sx={{ display: 'block', color: 'gray', marginTop: '0.5rem' }}
              >
                {t('legend_diagnostics', {
                  considered: diagnostics.harvestsConsidered,
                  withData: diagnostics.harvestsWithLegendData,
                })}
                {diagnostics.outliersRejected
                  ? ` ${t('legend_outliers_rejected', {
                      count: diagnostics.outliersRejected,
                    })}`
                  : ''}
                {diagnostics.excludedYears?.length
                  ? ` ${t('legend_excluded_years', {
                      years: diagnostics.excludedYears.join(', '),
                    })}`
                  : ''}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>

      <ModalActions>
        {[
          <Button
            key="cancel"
            variant="contained"
            color="secondary"
            onClick={() => dispatch(closeModal())}
            data-testid={TEST_IDS.generateCropLegendsCancelButton}
          >
            {t('cancel')}
          </Button>,
          ...(proposals && proposals.length > 1
            ? [
                <Button
                  key="apply-all"
                  variant="contained"
                  color="primary"
                  onClick={applyAll}
                  disabled={saving.length > 0}
                  data-testid={TEST_IDS.generateCropLegendsApplyAllButton}
                >
                  {t('legend_apply_all')}
                </Button>,
              ]
            : []),
          <Button
            key="run"
            variant="contained"
            color="primary"
            onClick={run}
            disabled={loading || !canRun}
            data-testid={TEST_IDS.generateCropLegendsRunButton}
          >
            {t('generate')}
          </Button>,
        ]}
      </ModalActions>
    </ModalContainer>
  )
}
